import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import {
  Merge,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Loader2,
  X,
  Layers,
  Users,
  GraduationCap,
  School as SchoolIcon,
  ListChecks,
  ArrowRight,
  Sparkles,
  Check,
  XCircle,
  RotateCcw,
  SlidersHorizontal,
  Lock,
} from "lucide-react";
import QuickQuizService from "../../_services/QuickQuizService";

interface MergeTestsPanelProps {
  onBack: () => void;
  onView: (testId: number) => void;
}

interface MergeResult {
  status: "success" | "error";
  message: string;
  targetTestId?: number;
  targetTestName?: string;
  createdSectionCount?: number;
  totalMarks?: number;
  isNewTest?: boolean;
  insertedStudentSections?: number;
  uniqueStudents?: number;
  uniqueSections?: number;
  insertedCourseMappings?: number;
  uniqueCourses?: number;
  uniqueBatches?: number;
  insertedSchoolMappings?: number;
  uniqueSchools?: number;
}

type IdState = "checking" | "found" | "missing";
interface IdInfo {
  state: IdState;
  name?: string;
  categoryName?: string | null;
  totalMarks?: number | null;
  totalQuestions?: number | null;
}

const parseTokens = (raw: string): number[] =>
  raw
    .split(/[\s,\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isInteger(n) && n > 0);

const MergeTestsPanel: React.FC<MergeTestsPanelProps> = ({ onBack, onView }) => {
  const location = useLocation();
  // One-shot prefill from URL query — populated when the user lands here from
  // the test-structure page's Merge button. We only read these on mount; the
  // user is free to add/remove IDs afterwards (but the target category, when
  // supplied, stays locked because that defines which column we're filling).
  const initialFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawIds = params.get("sourceTestIds") || "";
    const ids = parseTokens(rawIds);
    const targetCategoryId = Number(params.get("targetCategoryId")) || 0;
    const targetCategoryName = params.get("targetCategoryName") || "";
    const testName = params.get("testName") || "";
    return { ids, targetCategoryId, targetCategoryName, testName };
    // Intentionally not re-reading on every render — this is mount-time prefill.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [testName, setTestName] = useState(initialFromUrl.testName);
  const [ids, setIds] = useState<number[]>(initialFromUrl.ids);
  const [draft, setDraft] = useState("");
  const [idInfo, setIdInfo] = useState<Record<number, IdInfo>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<MergeResult | null>(null);
  // Merge options — default ON (the "do everything" path).
  const [copyStudentMarks, setCopyStudentMarks] = useState(true);
  const [copyCourseMappings, setCopyCourseMappings] = useState(true);
  // Locked target category — set only when the user arrived here from the
  // test-structure merge button. Otherwise zero and the backend falls back to
  // the first source test's CategoryId.
  const lockedTargetCategoryId = initialFromUrl.targetCategoryId;
  const lockedTargetCategoryName = initialFromUrl.targetCategoryName;
  const inputRef = useRef<HTMLInputElement>(null);

  const dedup = (arr: number[]) => Array.from(new Set(arr));

  // Debounced existence check — fires whenever the ids array changes
  useEffect(() => {
    if (ids.length === 0) {
      setIdInfo({});
      return;
    }
    // Mark untracked ids as "checking"
    setIdInfo((prev) => {
      const next: Record<number, IdInfo> = { ...prev };
      for (const id of ids) {
        if (!next[id]) next[id] = { state: "checking" };
      }
      // Drop entries no longer present
      for (const k of Object.keys(next)) {
        if (!ids.includes(Number(k))) delete next[Number(k)];
      }
      return next;
    });

    const handle = setTimeout(async () => {
      try {
        const data = await QuickQuizService.lookupTests(ids);
        setIdInfo(() => {
          const next: Record<number, IdInfo> = {};
          for (const f of data.found) {
            next[f.id] = {
              state: "found",
              name: f.name,
              categoryName: f.categoryName,
              totalMarks: f.totalMarks,
              totalQuestions: f.totalQuestions,
            };
          }
          for (const m of data.missing) {
            next[m] = { state: "missing" };
          }
          return next;
        });
      } catch {
        // On lookup failure, leave entries as 'checking' so user can still try
      }
    }, 350);

    return () => clearTimeout(handle);
  }, [ids]);

  // Derived counts
  const validIds = useMemo(
    () => ids.filter((id) => idInfo[id]?.state === "found"),
    [ids, idInfo],
  );
  // First-source category (used as the target when no explicit one is locked).
  const inferredCategoryName = useMemo(() => {
    if (lockedTargetCategoryId > 0) return "";
    for (const id of ids) {
      const info = idInfo[id];
      if (info?.state === "found" && info.categoryName) return info.categoryName;
    }
    return "";
  }, [ids, idInfo, lockedTargetCategoryId]);
  const missingIds = useMemo(
    () => ids.filter((id) => idInfo[id]?.state === "missing"),
    [ids, idInfo],
  );
  const checkingCount = useMemo(
    () => ids.filter((id) => idInfo[id]?.state === "checking").length,
    [ids, idInfo],
  );

  const commitDraft = (): boolean => {
    const tokens = parseTokens(draft);
    if (tokens.length === 0) return false;
    setIds((prev) => dedup([...prev, ...tokens]));
    setDraft("");
    return true;
  };
  const removeId = (id: number) => setIds((prev) => prev.filter((x) => x !== id));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "Tab") {
      const committed = commitDraft();
      if (committed) e.preventDefault();
    } else if (e.key === "Backspace" && draft === "" && ids.length > 0) {
      e.preventDefault();
      setIds((prev) => prev.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const txt = e.clipboardData.getData("text");
    const tokens = parseTokens(txt);
    if (tokens.length > 0) {
      e.preventDefault();
      setIds((prev) => dedup([...prev, ...tokens]));
      setDraft("");
    }
  };

  const handleClear = () => {
    setTestName("");
    setIds([]);
    setDraft("");
    setIdInfo({});
    setResult(null);
    setCopyStudentMarks(true);
    setCopyCourseMappings(true);
  };
  const handleStartOver = () => {
    handleClear();
  };

  const openConfirm = () => {
    // Auto-commit any pending draft
    const pending = parseTokens(draft);
    if (pending.length > 0) {
      setIds((prev) => dedup([...prev, ...pending]));
      setDraft("");
    }
    setConfirmOpen(true);
  };

  const doMerge = async () => {
    setConfirmOpen(false);
    if (validIds.length === 0) return;
    setSubmitting(true);
    setResult(null);
    try {
      const payload: {
        sourceTestIds: number[];
        testName?: string;
        targetCategoryId?: number;
        copyStudentMarks: boolean;
        copyCourseMappings: boolean;
      } = {
        sourceTestIds: validIds,
        copyStudentMarks,
        copyCourseMappings,
      };
      const trimmed = testName.trim();
      if (trimmed) payload.testName = trimmed;
      if (lockedTargetCategoryId > 0) payload.targetCategoryId = lockedTargetCategoryId;
      const data = await QuickQuizService.mergeSequenceTests(payload);
      setResult(data as MergeResult);
    } catch (err: any) {
      setResult({
        status: "error",
        message:
          err?.response?.data?.message || err?.message || "An error occurred while merging tests.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSuccess = result?.status === "success";
  const isResolved = !!result;

  // Allow confirm only when at least one valid ID, none still checking, and no missing ones
  const canSubmit =
    !submitting && validIds.length > 0 && checkingCount === 0 && missingIds.length === 0;

  // Step strip status
  const stepStatus = useMemo(() => {
    const idsDone = validIds.length > 0;
    return {
      step1: isResolved ? "done" : idsDone ? "done" : "active",
      step2: isResolved ? "done" : idsDone ? "active" : "todo",
      step3: isResolved ? "active" : "todo",
    } as const;
  }, [validIds.length, isResolved]);

  return (
    <div className="qmt-page">
      <div className="qmt-card">
        {/* ===== Hero ===== */}
        <div className="qmt-hero">
          <div className="qmt-hero-deco" aria-hidden="true">
            <span className="qmt-hero-ring qmt-hero-ring-1" />
            <span className="qmt-hero-ring qmt-hero-ring-2" />
            <span className="qmt-hero-ring qmt-hero-ring-3" />
          </div>
          <div className="qmt-hero-inner">
            <div className="qmt-hero-icon">
              <Merge size={22} strokeWidth={2.25} />
            </div>
            <div className="qmt-hero-text">
              <span className="qmt-hero-eyebrow">
                <Sparkles size={11} /> Quick Quiz Maker
              </span>
              <h2 className="qmt-hero-title">Merge Sequence Tests</h2>
              <p className="qmt-hero-sub">
                Fold multiple source tests into a single target — sections, student marks, and
                course / school mappings travel with them.
              </p>
            </div>
          </div>
          <ol className="qmt-steps">
            <li className={`qmt-step is-${stepStatus.step1}`}>
              <span className="qmt-step-num">{stepStatus.step1 === "done" ? <Check size={12} /> : 1}</span>
              <span className="qmt-step-label">Pick source tests</span>
            </li>
            <li className={`qmt-step is-${stepStatus.step2}`}>
              <span className="qmt-step-num">{stepStatus.step2 === "done" ? <Check size={12} /> : 2}</span>
              <span className="qmt-step-label">Name the target</span>
            </li>
            <li className={`qmt-step is-${stepStatus.step3}`}>
              <span className="qmt-step-num">3</span>
              <span className="qmt-step-label">Merge &amp; verify</span>
            </li>
          </ol>
        </div>

        {/* ===== Body — form OR success takeover ===== */}
        {isSuccess ? (
          <div className="qmt-done">
            <div className="qmt-done-top">
              <div className="qmt-done-badge">
                <CheckCircle2 size={28} strokeWidth={2.25} />
              </div>
              <div className="qmt-done-headline">
                <span className="qmt-done-eyebrow">Saved · Test #{result.targetTestId} created</span>
                <h3 className="qmt-done-title">Merge completed successfully</h3>
                <p className="qmt-done-sub">
                  <strong>{result.targetTestName}</strong> — built from {validIds.length} source
                  test{validIds.length === 1 ? "" : "s"}.
                </p>
              </div>
            </div>

            <div className="qmt-stat-grid qmt-stat-grid-done">
              <StatTile
                icon={<Layers size={16} />}
                label="Sections"
                value={result.createdSectionCount ?? 0}
                sub={`Total marks: ${result.totalMarks ?? 0}`}
                tone="indigo"
              />
              <StatTile
                icon={<Users size={16} />}
                label="Student rows"
                value={result.insertedStudentSections ?? 0}
                sub={
                  result.uniqueStudents != null
                    ? `${result.uniqueStudents} student${result.uniqueStudents === 1 ? "" : "s"}`
                    : undefined
                }
                tone="emerald"
              />
              <StatTile
                icon={<GraduationCap size={16} />}
                label="Course mappings"
                value={result.insertedCourseMappings ?? 0}
                sub={
                  result.uniqueCourses != null && result.uniqueBatches != null
                    ? `${result.uniqueCourses} courses · ${result.uniqueBatches} batches`
                    : undefined
                }
                tone="violet"
              />
              <StatTile
                icon={<SchoolIcon size={16} />}
                label="School mappings"
                value={result.insertedSchoolMappings ?? 0}
                sub={
                  result.uniqueSchools != null
                    ? `${result.uniqueSchools} school${result.uniqueSchools === 1 ? "" : "s"}`
                    : undefined
                }
                tone="amber"
              />
            </div>

            {result.message && (
              <div className="qmt-result-message">
                <ListChecks size={13} className="qmt-result-message-icon" />
                <span>{result.message}</span>
              </div>
            )}

            <div className="qmt-done-actions">
              <a
                href={`/quick-quiz-maker?mode=view&testId=${result.targetTestId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="qmt-btn qmt-btn-primary"
              >
                Open Test #{result.targetTestId}
                <ExternalLink size={15} />
              </a>
              <button type="button" className="qmt-btn qmt-btn-ghost" onClick={handleStartOver}>
                <RotateCcw size={15} />
                Merge Another
              </button>
              <button type="button" className="qmt-btn qmt-btn-text" onClick={onBack}>
                Back to Manage Tests
              </button>
            </div>
          </div>
        ) : (
          <div className="qmt-body">
            {/* Source IDs */}
            <section className="qmt-section">
              <header className="qmt-section-head">
                <div>
                  <span className="qmt-section-step">Step 1</span>
                  <h3 className="qmt-section-title">Source test IDs</h3>
                  <p className="qmt-section-sub">
                    Type or paste IDs — press Enter, comma, or space to add each one. We'll verify
                    they exist as you type.
                  </p>
                </div>
                <div className="qmt-count-stack">
                  <span className={`qmt-count ${validIds.length > 0 ? "is-ok" : ""}`}>
                    {validIds.length} valid
                  </span>
                  {missingIds.length > 0 && (
                    <span className="qmt-count is-bad">{missingIds.length} not found</span>
                  )}
                </div>
              </header>

              <div
                className={`qmt-tagbox ${ids.length > 0 ? "has-tags" : ""}`}
                onClick={() => inputRef.current?.focus()}
              >
                {ids.map((id) => {
                  const info = idInfo[id];
                  const state = info?.state ?? "checking";
                  const tooltip =
                    state === "found"
                      ? `${info?.name}${info?.categoryName ? ` · ${info.categoryName}` : ""}`
                      : state === "missing"
                        ? `Test #${id} not found`
                        : "Checking…";
                  return (
                    <span key={id} className={`qmt-tag is-${state}`} title={tooltip}>
                      <span className="qmt-tag-state" aria-hidden="true">
                        {state === "checking" && <Loader2 size={11} className="qmt-spin" />}
                        {state === "found" && <Check size={11} strokeWidth={3} />}
                        {state === "missing" && <XCircle size={11} strokeWidth={2.5} />}
                      </span>
                      <span className="qmt-tag-id">{id}</span>
                      {state === "found" && info?.name && (
                        <span className="qmt-tag-name">{info.name}</span>
                      )}
                      <button
                        type="button"
                        className="qmt-tag-x"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeId(id);
                        }}
                        aria-label={`Remove ${id}`}
                        disabled={submitting}
                      >
                        <X size={11} strokeWidth={2.5} />
                      </button>
                    </span>
                  );
                })}
                <input
                  ref={inputRef}
                  type="text"
                  className="qmt-tag-input"
                  placeholder={ids.length === 0 ? "e.g. 672, 673, 1234" : ""}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onBlur={() => parseTokens(draft).length > 0 && commitDraft()}
                  disabled={submitting}
                  inputMode="numeric"
                />
              </div>

              {missingIds.length > 0 && (
                <div className="qmt-banner is-bad">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{missingIds.length}</strong> test{missingIds.length === 1 ? "" : "s"}{" "}
                    not found: {missingIds.join(", ")} — remove these before continuing.
                  </span>
                </div>
              )}
            </section>

            {/* Test name */}
            <section className="qmt-section">
              <header className="qmt-section-head">
                <div>
                  <span className="qmt-section-step">Step 2</span>
                  <h3 className="qmt-section-title">Merged test name</h3>
                  <p className="qmt-section-sub">
                    Leave blank for an auto-generated timestamped name.
                  </p>
                </div>
              </header>
              <input
                type="text"
                className="qmt-input"
                placeholder="Optional — e.g. Phase 1 + Phase 2 Merged"
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                disabled={submitting}
              />

              {/* Target category card — sits right below the test name so the
                  user sees what category the new test will land in while they
                  decide on a name. Locked when arriving from the test-structure
                  page; otherwise inferred from the first valid source test's
                  category, which mirrors the backend's fallback. */}
              {(lockedTargetCategoryId > 0 || inferredCategoryName) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    marginTop: 10,
                    borderRadius: 10,
                    background: lockedTargetCategoryId > 0 ? "#eef2ff" : "#f8fafc",
                    border: `1px solid ${lockedTargetCategoryId > 0 ? "#c7d2fe" : "#e2e8f0"}`,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: lockedTargetCategoryId > 0 ? "#4f46e5" : "#94a3b8",
                      color: "var(--text-on-brand)",
                    }}
                  >
                    {lockedTargetCategoryId > 0 ? <Lock size={14} /> : <Layers size={14} />}
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-body)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Target test category
                      {lockedTargetCategoryId > 0
                        ? " · locked"
                        : " · derived from first source test"}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-strong)" }}>
                      {lockedTargetCategoryId > 0
                        ? (lockedTargetCategoryName || `Category #${lockedTargetCategoryId}`)
                        : inferredCategoryName}
                    </span>
                  </div>
                </div>
              )}
            </section>

            {/* Step 3 — Merge options (spans both columns) */}
            <section className="qmt-section qmt-section-options">
              <header className="qmt-section-head">
                <div>
                  <span className="qmt-section-step">
                    <SlidersHorizontal size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                    Step 3
                  </span>
                  <h3 className="qmt-section-title">Merge options</h3>
                  <p className="qmt-section-sub">
                    Pick what should be carried into the merged test. Sections are always copied.
                  </p>
                </div>
              </header>

              <div className="qmt-option-grid">
                <ToggleOption
                  checked={copyStudentMarks}
                  onChange={setCopyStudentMarks}
                  disabled={submitting}
                  icon={<Users size={18} />}
                  tone="emerald"
                  title="Copy student marks"
                  description="Aggregate per-student marks from each source test (parent rows are summed across sources, child rows mapped 1:1)."
                />
                <ToggleOption
                  checked={copyCourseMappings}
                  onChange={setCopyCourseMappings}
                  disabled={submitting}
                  icon={<GraduationCap size={18} />}
                  tone="violet"
                  title="Copy course mappings"
                  description="Carry the source tests' unique course / program / batch / term mappings into the merged test."
                />
              </div>
            </section>

            {/* Action footer */}
            <div className="qmt-actions">
              <button
                type="button"
                className="qmt-btn qmt-btn-text"
                onClick={onBack}
                disabled={submitting}
              >
                ← Back to Manage Tests
              </button>
              <div className="qmt-actions-right">
                <button
                  type="button"
                  className="qmt-btn qmt-btn-ghost"
                  onClick={handleClear}
                  disabled={
                    submitting && !(ids.length === 0 && !testName && !draft && !result)
                  }
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="qmt-btn qmt-btn-primary"
                  onClick={openConfirm}
                  disabled={!canSubmit}
                  title={
                    missingIds.length > 0
                      ? "Remove the missing test IDs first"
                      : checkingCount > 0
                        ? "Verifying IDs…"
                        : validIds.length === 0
                          ? "Add at least one valid test ID"
                          : "Review and confirm the merge"
                  }
                >
                  {submitting ? (
                    <>
                      <Loader2 size={15} className="qmt-spin" />
                      Merging…
                    </>
                  ) : (
                    <>
                      Review &amp; Merge
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Error result */}
            {result && !isSuccess && (
              <div className="qmt-result is-error">
                <div className="qmt-result-head">
                  <span className="qmt-result-icon">
                    <AlertTriangle size={22} />
                  </span>
                  <div className="qmt-result-headline">
                    <strong>Merge failed</strong>
                  </div>
                </div>
                <p className="qmt-error-text">{result.message}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========== Confirmation modal ========== */}
      <Modal show={confirmOpen} onHide={() => setConfirmOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700 }}>
            Confirm merge
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13.5, color: "var(--text-body)", marginBottom: 14 }}>
            A new test will be created from the following <strong>{validIds.length}</strong>{" "}
            source test{validIds.length === 1 ? "" : "s"}. Sections, student marks, and course /
            school mappings will be carried into the new test.
          </p>

          <div className="qmt-confirm-list">
            {validIds.map((id) => {
              const info = idInfo[id];
              return (
                <div key={id} className="qmt-confirm-row">
                  <span className="qmt-confirm-id">#{id}</span>
                  <div className="qmt-confirm-meta">
                    <span className="qmt-confirm-name">{info?.name || `Test ${id}`}</span>
                    <span className="qmt-confirm-sub">
                      {info?.categoryName ? `${info.categoryName} · ` : ""}
                      {info?.totalMarks != null ? `${info.totalMarks} marks` : ""}
                      {info?.totalQuestions != null ? ` · ${info.totalQuestions} questions` : ""}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 16,
              padding: "12px 14px",
              borderRadius: 10,
              background: "var(--brand-primary-subtle)",
              border: "1px solid var(--brand-primary-subtle)",
              color: "var(--brand-primary-active)",
              fontSize: 12.5,
              lineHeight: 1.55,
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
            }}
          >
            <ListChecks size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: 4 }}>
                Target name:{" "}
                <strong style={{ color: "#1e1b4b" }}>
                  {testName.trim() || `Merged Test - ${new Date().toISOString().slice(0, 19).replace("T", " ")}`}
                </strong>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                <span className="qmt-pill qmt-pill-on">
                  <Check size={10} strokeWidth={3} /> Sections
                </span>
                <span className={`qmt-pill ${copyStudentMarks ? "qmt-pill-on" : "qmt-pill-off"}`}>
                  {copyStudentMarks ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} />}
                  Student marks
                </span>
                <span className={`qmt-pill ${copyCourseMappings ? "qmt-pill-on" : "qmt-pill-off"}`}>
                  {copyCourseMappings ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} />}
                  Course mappings
                </span>
                <span className="qmt-pill qmt-pill-on">
                  <Check size={10} strokeWidth={3} /> School mappings
                </span>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={doMerge}
            style={{
              background: "var(--gradient-brand)",
              border: "none",
              fontWeight: 600,
            }}
          >
            <Merge size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Yes, Merge {validIds.length} Test{validIds.length === 1 ? "" : "s"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub?: string;
  tone: "indigo" | "emerald" | "violet" | "amber";
}
const StatTile: React.FC<StatTileProps> = ({ icon, label, value, sub, tone }) => (
  <div className={`qmt-stat qmt-stat-${tone}`}>
    <span className="qmt-stat-icon">{icon}</span>
    <span className="qmt-stat-label">{label}</span>
    <span className="qmt-stat-value">{value.toLocaleString()}</span>
    {sub && <span className="qmt-stat-sub">{sub}</span>}
  </div>
);

interface ToggleOptionProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  icon: React.ReactNode;
  tone: "emerald" | "violet" | "indigo" | "amber";
  title: string;
  description: string;
}
const ToggleOption: React.FC<ToggleOptionProps> = ({
  checked,
  onChange,
  disabled,
  icon,
  tone,
  title,
  description,
}) => (
  <label
    className={`qmt-option qmt-option-${tone} ${checked ? "is-on" : "is-off"} ${disabled ? "is-disabled" : ""}`}
  >
    <input
      type="checkbox"
      className="qmt-option-input"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
    <span className="qmt-option-check" aria-hidden="true">
      {checked && <Check size={12} strokeWidth={3} />}
    </span>
    <span className="qmt-option-icon">{icon}</span>
    <span className="qmt-option-body">
      <span className="qmt-option-title">{title}</span>
      <span className="qmt-option-desc">{description}</span>
    </span>
  </label>
);

export default MergeTestsPanel;
